import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Badge}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @today/tokens
 * (resolved per bound brand). No hardcoded values.
 *
 * @remarks
 * - Supports five variants: primary, secondary, danger, success, muted
 * - Supports three sizes: sm, md (default), lg
 * - Inline-flex for text and optional icon
 *
 * @example
 * ```tsx
 * const classes = badgeVariants({ variant: 'primary', size: 'md' })
 * // Returns: "inline-flex items-center rounded-full ... bg-ds-primary ..."
 * ```
 */
export const badgeVariants = cva('inline-flex items-center justify-center font-normal rounded-full', {
	variants: {
		variant: {
			primary: 'bg-ds-primary text-ds-on-primary',
			secondary: 'bg-ds-secondary text-ds-on-primary',
			danger: 'bg-ds-danger text-ds-on-danger',
			success: 'bg-ds-success text-ds-on-success',
			muted: 'bg-ds-surface-muted text-ds-text border border-ds-border',
		},
		size: {
			sm: 'text-ds-sm px-2 py-0.5 h-5',
			md: 'text-ds-base px-3 py-1 h-6',
			lg: 'text-ds-lg px-4 py-1.5 h-8',
		},
	},
	defaultVariants: { variant: 'primary', size: 'md' },
})

/**
 * Props for the {@link Badge} component.
 *
 * @property variant - Visual style: 'primary' | 'secondary' | 'danger' | 'success' | 'muted'
 * @property size - Badge size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property children - Badge text content
 *
 * @example
 * ```tsx
 * <Badge variant="primary" size="md">
 *   New
 * </Badge>
 * ```
 */
export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

/**
 * Badge component for displaying status, labels, or counts.
 *
 * @remarks
 * - **Styling:** All colors and sizing use design tokens from `@today/tokens`
 *   (resolved per bound brand). No hardcoded values.
 * - **Variants:** Primary (brand primary colour), secondary, danger, success, muted.
 * - **Sizes:** Small (h-5), medium (h-6, default), large (h-8).
 * - **Shape:** Rounded-full (pill shape).
 * - **Usage:** Status indicators, labels, notification counts, category tags.
 *
 * @example Basic usage
 * ```tsx
 * import { Badge } from '@today/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       <Badge variant="primary">New</Badge>
 *       <Badge variant="success">Active</Badge>
 *       <Badge variant="danger">Error</Badge>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With different sizes
 * ```tsx
 * <Badge size="sm" variant="muted">Small</Badge>
 * <Badge size="md">Medium</Badge>
 * <Badge size="lg" variant="success">Large</Badge>
 * ```
 *
 * @see {@link badgeVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/badge/badge.stories.tsx for visual examples
 */
export function Badge({ variant, size, className, ...props }: BadgeProps) {
	return <span {...props} className={cn(badgeVariants({ variant, size }), className)} />
}
